import type { JSX } from 'react'
import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import type { RegisterRequest } from '@/lib/dto'
import { api } from '@/lib/api'
import { ApiError } from '@/lib/api-error'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { Button } from '@/ui/primitives/Button'
import { PasswordField, TextField } from '@/ui/primitives/Field'
import { fieldErrorsOf, problemText } from '../shared/problem'
import { AuthShell } from './AuthShell'

/**
 * Регистрация.
 *
 * Пароль проверяем по правилу СЕРВЕРА — не короче 12 символов (`PasswordSchema`
 * в `apps/api`). Прежний экран требовал 10, и последний символ отбирал сервер:
 * форма говорила «всё хорошо», а запрос падал с `VALIDATION_FAILED`.
 *
 * Ошибки полей от сервера раскладываются по тем же полям, что и клиентская
 * валидация (`fieldErrorsOf`): иначе «некорректный email» приезжает общим
 * баннером сверху, и пользователь ищет, что именно исправлять.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MIN_PASSWORD = 12

export function SignUpScreen(): JSX.Element {
  const navigate = useNavigate()
  const push = useToastStore(state => state.push)

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const register = useMutation({
    mutationFn: (dto: RegisterRequest) => api.auth.register(dto),
    onSuccess: async () => {
      push({
        message: 'Аккаунт создан — теперь войдите',
        tone: 'success',
        timeoutMs: DEFAULT_TOAST_MS
      })
      await navigate({ to: '/login' })
    },
    onError: error => {
      const fields = fieldErrorsOf(error)
      if (Object.keys(fields).length > 0) {
        setErrors(fields)
        setFormError('Проверьте выделенные поля')
        return
      }
      if (error instanceof ApiError && error.code === 'SIGNUP_CLOSED') {
        setFormError('Регистрация закрыта. Попросите у владельца панели инвайт-код.')
        return
      }
      setFormError(problemText(error))
    }
  })

  const validate = (): boolean => {
    const found: Record<string, string> = {}
    if (displayName.trim().length < 2) found.displayName = 'Хотя бы два символа'
    if (!EMAIL_RE.test(email.trim())) found.email = 'Похоже, это не адрес почты'
    if (password.length < MIN_PASSWORD) found.password = `Не короче ${MIN_PASSWORD} символов`
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const longEnough = password.length >= MIN_PASSWORD

  return (
    <AuthShell
      title="Регистрация"
      subtitle="Панель self-hosted: аккаунт живёт на вашем сервере и никуда не отправляется."
      footer={
        <>
          Уже есть аккаунт?{' '}
          <Link to="/login" className="text-honey hover:underline">
            Войти
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={event => {
          event.preventDefault()
          setFormError(null)
          if (!validate()) return
          register.mutate({
            email: email.trim(),
            password,
            displayName: displayName.trim(),
            ...(inviteCode.trim() === '' ? {} : { inviteCode: inviteCode.trim() })
          })
        }}
      >
        <TextField
          label="Как вас называть"
          autoComplete="name"
          value={displayName}
          required
          error={errors.displayName ?? null}
          onBlur={() => {
            if (displayName !== '') validate()
          }}
          onChange={event => setDisplayName(event.target.value)}
        />

        <TextField
          label="Почта"
          type="email"
          autoComplete="username"
          mono
          value={email}
          required
          error={errors.email ?? null}
          onBlur={() => {
            if (email !== '') validate()
          }}
          onChange={event => setEmail(event.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <PasswordField
            label="Пароль"
            autoComplete="new-password"
            value={password}
            required
            error={errors.password ?? null}
            onChange={event => setPassword(event.target.value)}
          />
          {/* Живая подсказка вместо «пароль слишком короткий» после отправки:
              видно, сколько осталось, пока печатаешь. */}
          <p
            role="status"
            className={longEnough ? 'text-2xs text-live' : 'text-2xs text-ink-muted'}
          >
            {password === ''
              ? `Не короче ${MIN_PASSWORD} символов — это требование сервера`
              : longEnough
                ? 'Длины достаточно'
                : `Ещё ${MIN_PASSWORD - password.length} символов`}
          </p>
        </div>

        <TextField
          label="Инвайт-код (если есть)"
          mono
          hint="Нужен, когда владелец закрыл свободную регистрацию"
          value={inviteCode}
          error={errors.inviteCode ?? null}
          onChange={event => setInviteCode(event.target.value)}
        />

        {formError === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          className="mt-1 w-full"
          loading={register.isPending}
        >
          Создать аккаунт
        </Button>
      </form>
    </AuthShell>
  )
}
